// Laedt das Spotify Web Playback SDK — genau einmal.
//
// Warum ueberhaupt: Laeuft die Wiedergabe in der Spotify-App auf demselben
// Handy, das auch das Quiz bedient, friert iOS die Spotify-App wenige
// Sekunden nach dem Pausieren ein. Sie meldet sich dann von Spotify Connect
// ab und ist aus der Geraeteliste verschwunden (gemessen: 5–10 Sekunden).
// Wird die Quiz-Seite selbst zum Wiedergabegeraet, gibt es keine
// Hintergrund-App mehr, die eingefroren werden koennte.
//
// Das SDK meldet sich ueber einen GLOBALEN Haken zurueck
// (window.onSpotifyWebPlaybackSDKReady). Genau daran scheitert der naive
// Ansatz: bei jedem Re-Render ein neues <script>-Tag, mehrere Player, mehrere
// Geraete in der Liste. Deshalb hier ein Promise, das gecacht wird.

const SDK_URL = "https://sdk.scdn.co/spotify-player.js";

// Name, unter dem die Quiz-Seite in Spotifys Geraeteliste auftaucht.
// An einer Stelle definiert, weil die App danach auch Leichen aussortiert:
// nach einem Reload haengt der alte Eintrag bei Spotify noch ein paar Minuten
// nach und stuende sonst doppelt in der Auswahl.
export const PLAYER_NAME = "Vinyl Showdown";
// Frueherer Name: Spieler, die noch die alte Seite offen haben, stehen bis
// zum naechsten Laden unter ihm in der Liste — auch die gelten als Leiche.
const ALTE_NAMEN = ["Robins Musikquiz"];
/** Ist dieser Spotify-Eintrag ein Quiz-Player (egal ob aktuell oder alt)? */
export const istQuizPlayer = (name) => name === PLAYER_NAME || ALTE_NAMEN.includes(name);

let ladePromise = null;

export function ladeSpotifySdk() {
  if (window.Spotify && window.Spotify.Player) {
    return Promise.resolve(window.Spotify);
  }
  if (ladePromise) return ladePromise;

  ladePromise = new Promise((resolve, reject) => {
    // Nach einem Fehlschlag muss das <script> wieder weg: sonst fand der
    // naechste Versuch es im DOM, wartete nur noch auf den Haken und lief
    // jedes Mal in den Timeout — Wiedergabe im Browser tot bis zum Neuladen.
    const scriptEntfernen = () => {
      const tag = document.querySelector(`script[src="${SDK_URL}"]`);
      if (tag) tag.remove();
    };

    const zeitueberschreitung = setTimeout(() => {
      ladePromise = null;
      scriptEntfernen();
      reject(new Error("sdk_timeout"));
    }, 15000);

    // Einen bereits gesetzten Haken nicht einfach ueberschreiben.
    const vorher = window.onSpotifyWebPlaybackSDKReady;
    window.onSpotifyWebPlaybackSDKReady = () => {
      clearTimeout(zeitueberschreitung);
      if (typeof vorher === "function") {
        try {
          vorher();
        } catch (e) { /* fremder Haken — nicht unser Problem */ }
      }
      resolve(window.Spotify);
    };

    const vorhandenes = document.querySelector(`script[src="${SDK_URL}"]`);
    if (vorhandenes) return; // laeuft schon, der Haken oben faengt es auf

    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onerror = () => {
      clearTimeout(zeitueberschreitung);
      ladePromise = null;
      scriptEntfernen();
      reject(new Error("sdk_laden_fehlgeschlagen"));
    };
    document.head.appendChild(script);
  });

  return ladePromise;
}

/** Klartext fuer die Fehler, die das SDK melden kann. */
export const SDK_FEHLERTEXTE = {
  initialization_error: "Dieser Browser kann keine Musik abspielen.",
  authentication_error: "Spotify-Anmeldung abgelehnt — bitte neu verbinden.",
  account_error: "Dafuer braucht es Spotify Premium.",
  playback_error: "Wiedergabe hier nicht moeglich.",
  sdk_timeout: "Spotify-Wiedergabe konnte nicht geladen werden.",
  sdk_laden_fehlgeschlagen: "Spotify-Wiedergabe konnte nicht geladen werden.",
};

/**
 * Verraet dem Betriebssystem NICHT, was gerade laeuft.
 *
 * Sobald Audio spielt, zeigt iOS Titel, Interpret und Cover in Dynamic
 * Island, Sperrbildschirm und Kontrollzentrum an — beim Host also genau die
 * Loesung, direkt neben dem Quiz. Abschalten laesst sich diese Anzeige nicht,
 * ihr Inhalt aber schon: die MediaSession-Metadaten gehoeren uns, wenn wir
 * sie selbst setzen. Das SDK schreibt bei jedem Titelwechsel den echten Song
 * hinein, deshalb muss das hier nach JEDEM Zustandswechsel erneut passieren.
 */
// Die Systemanzeige (Dynamic Island, Sperrbildschirm) liest
// navigator.mediaSession.metadata. Das SDK schreibt dort bei jedem
// Titelwechsel den echten Song samt Cover hinein — und verriet so die
// Loesung. Frueher haben wir danach mehrfach zurueckgeschrieben (Wettlauf,
// das Cover blitzte trotzdem auf). Jetzt wird der Setter auf dieser Seite
// abgefangen: nur unsere eigenen Metadaten kommen durch.
let unsereMetadaten = null;
let sperreAktiv = false;
function sperreMetadaten() {
  if (sperreAktiv) return;
  try {
    const ms = navigator.mediaSession;
    const desc = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(ms), "metadata");
    if (!desc || !desc.set || !desc.get) return;
    Object.defineProperty(ms, "metadata", {
      configurable: true,
      get() {
        return desc.get.call(ms);
      },
      set(wert) {
        if (wert === unsereMetadaten) desc.set.call(ms, wert);
      },
    });
    sperreAktiv = true;
  } catch (e) {
    // Browser laesst es nicht zu — dann bleibt es beim Zurueckschreiben
  }
}

export function verbergeSongInfo() {
  if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
  try {
    if (!unsereMetadaten) {
      const bild = `${window.location.origin}/logo512.png`;
      unsereMetadaten = new window.MediaMetadata({
        title: PLAYER_NAME,
        artist: "Titel wird nicht verraten",
        album: "",
        artwork: [{ src: bild, sizes: "512x512", type: "image/png" }],
      });
    }
    sperreMetadaten();
    navigator.mediaSession.metadata = unsereMetadaten;
  } catch (e) {
    // Aeltere Browser kennen MediaMetadata nicht — dann bleibt es beim
    // Standard. Kein Grund, die Wiedergabe zu stoeren.
  }
}
